import { Progress, Tooltip } from 'antd';
import { useState } from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { CaretDown, CaretUp, Eye, EyeSlash } from '@phosphor-icons/react';
import { useHistory } from 'react-router-dom';
import { IWalletProps } from '../../../../../../../utils/@types/wallet';
import { useActives } from '../../../../../../../hooks/useActives';
import { defineDisplayColor } from '../../../../DefineDisplayColor';
import { RenderConditionalObjective } from '../../../../../RenderConditionalObjective';
import { filterTableSorte } from '../../../../../../../utils/filterTableSort';
import { transformMaskCurrency } from '../../../../../../../utils/transformMaskCurrency';
import { ButtonComponent } from '../../../../../../../components/ButtonComponent';
import { GraphicNote } from '../../../../../../pages/Wallet/Tabs/AllActive/components/AllTable/GraphicNote';
import { CompanyNameContent } from './styles';
import { BiInfoCircle } from 'react-icons/bi';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#FFF',
    color: theme.palette.common.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));
const header = [
  {
    label: 'Ativo',
    active: 'tickerSymbol',
  },
  {
    label: 'Nota',
    active: 'nota',
  },
  {
    label: 'Quant.',
    active: 'equitiesQuantity',
  },
  {
    label: 'Patrimônio',
    active: 'updateValue',
    help: 'Valor atual investido no ativo.',
  },
  {
    label: 'Cotação',
    active: 'closingPrice',
  },
  { label: 'Objetivo', help: 'Percentual desejado de alocação na carteira.' },

  {
    label: 'Atual',
    active: 'percent',
    help: 'Percentual atual de alocação na carteira.',
  },
  {
    label: 'Diferença',
    active: 'gap',
    help: 'Diferença entre o percentual atual e o objetivo.',
  },
  {
    label: 'Preço médio',
    active: 'PrecoMedio',
    help: 'O preço médio é calculado somando-se o custo total de todas as compras de um ativo e dividindo-o pelo número total de ações ou cotas adquiridas.',
  },
  {
    label: 'Preço médio com proventos',
    active: 'Preco médio com proventos',
    help: 'Preco médio subtraindo os dividendos/proventos por cota recebidos.',
  },
  {
    label: 'Total de proventos recebidos',
    active: 'Total de proventos recebidos',
    help: 'Soma total de dividendos, JCP e rendimentos recebidos.',
  },
  {
    label: 'Total investido',
    active: 'Total investido',
    help: 'Valor total aportado no ativo ao longo do tempo.',
  },
  {
    label: 'Rentabilidade com proventos',
    active: 'Rentabilidade com proventos',
    help: 'Rentabilidade líquida considerando valorização e proventos.',
  },
  {
    label: 'Lucro/Prejuízo',
    active: 'lucro',
    help: 'Rentabilidade considerando o valor investido e os dividendos/proventos recebidos.',
  },
];

interface IPropsActionTable {
  walletValues: IWalletProps;
  setWalletValues: Function;
  filterBrokerage?: string;
  setRule: Function;
  setShowModalCreateRule: Function;
  setShowModalFilterColumnTable: Function;
  filterShowColumnTable: string[];
  filterTextSearchActive: string;
  /** Carteira unificada (/wallet): resolve o objetivo sempre pela regra
   * `type === 'all'`. Não usado na MyWallet (Boltz). */
  forceAllType?: boolean;
}

export function ActionsTable({
  walletValues,
  setWalletValues,
  setRule,
  setShowModalCreateRule,
  filterBrokerage,
  setShowModalFilterColumnTable,
  filterShowColumnTable,
  filterTextSearchActive,
  forceAllType = false,
}: IPropsActionTable) {
  //@ts-ignore
  const { showActive } = useActives();

  const [showAmountActives, setShowAmountActives] = useState<boolean>(false);
  const [showActiveName, setShowActiveName] = useState<boolean>(false);

  const showActiveColumnTable = filterShowColumnTable.includes('Ativo');
  const showNoteColumnTable = filterShowColumnTable.includes('Nota');
  const showAmountColumnTable = filterShowColumnTable.includes('Quant.');
  const showPatrimonyColumnTable = filterShowColumnTable.includes('Patrimônio');
  const showGoalColumnTable = filterShowColumnTable.includes('Objetivo');
  const showPriceColumnTable = filterShowColumnTable.includes('Cotação');
  const showCurrentColumnTable = filterShowColumnTable.includes('Atual');
  const showGapColumnTable = filterShowColumnTable.includes('Diferença');

  const showProfitColumnTable =
    filterShowColumnTable.includes('Lucro/Prejuízo');
  const showProfitabilityColumnTable =
    filterShowColumnTable.includes('Rentabilidade');
  const showTotalApliedColumnTable =
    filterShowColumnTable.includes('Total investido');
  //aqui
  const showRealProfitabilityColumnTable = filterShowColumnTable.includes(
    'Rentabilidade com proventos'
  );
  const showAveragePriceColumnTable =
    filterShowColumnTable.includes('Preço médio');
  const showTotalProfits = filterShowColumnTable.includes(
    'Total de proventos recebidos'
  );

  const showAveragePriceWithProfitsColumnTable = filterShowColumnTable.includes(
    'Preço médio com proventos'
  );

  const ArrayFilteredAllTabe = walletValues?.positionAcao?.filter((item) => {
    if (
      item.tickerSymbol
        .toLowerCase()
        .includes(filterTextSearchActive.toLocaleLowerCase()) ||
      item.corporationName
        .toLowerCase()
        .includes(filterTextSearchActive.toLocaleLowerCase())
    ) {
      return item;
    }
  });

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table aria-label="customized table" stickyHeader>
        <TableHead>
          <TableRow>
            {header.map((item) => {
              if (filterShowColumnTable.includes(item.label)) {
                return (
                  <StyledTableCell key={item.label}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '0.95rem',
                      }}
                    >
                      {item.label}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {item?.help && (
                          <Tooltip title={item.help}>
                            <BiInfoCircle size={16} color="#EC6643" />
                          </Tooltip>
                        )}
                      </div>
                      {item.active && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <CaretUp
                            size={14}
                            color="black"
                            onClick={() => {
                              setWalletValues({
                                ...walletValues,
                                //@ts-ignore
                                positionAcao: filterTableSorte({
                                  data: walletValues.positionAcao,
                                  dir: 'asc',
                                  //@ts-ignore
                                  key: item.active,
                                }),
                              });
                            }}
                          />
                          <CaretDown
                            size={14}
                            color="black"
                            onClick={() => {
                              setWalletValues({
                                ...walletValues,
                                //@ts-ignore
                                positionAcao: filterTableSorte({
                                  data: walletValues.positionAcao,
                                  dir: 'desc',
                                  //@ts-ignore
                                  key: item.active,
                                }),
                              });
                            }}
                          />
                        </div>
                      )}
                      {item.label === 'Quant.' &&
                        (showAmountActives ? (
                          <Eye
                            size={20}
                            color="black"
                            onClick={() =>
                              setShowAmountActives(!showAmountActives)
                            }
                          />
                        ) : (
                          <EyeSlash
                            size={20}
                            color="black"
                            onClick={() =>
                              setShowAmountActives(!showAmountActives)
                            }
                          />
                        ))}
                      {item.label === 'Ativo' &&
                        (showActiveName ? (
                          <Eye
                            size={20}
                            color="black"
                            onClick={() => setShowActiveName(!showActiveName)}
                          />
                        ) : (
                          <EyeSlash
                            size={20}
                            color="black"
                            onClick={() => setShowActiveName(!showActiveName)}
                          />
                        ))}
                    </div>
                  </StyledTableCell>
                );
              }
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {ArrayFilteredAllTabe && ArrayFilteredAllTabe.length > 0 ? (
            ArrayFilteredAllTabe.map((item, index) => (
              <StyledTableRow key={index}>
                {showActiveColumnTable && (
                  <StyledTableCell className="reduce link">
                    <CompanyNameContent>
                      {showActiveName || showActive ? (
                        <>
                          <strong title={item.tickerSymbol}>
                            {item.tickerSymbol}
                          </strong>
                          <span title={item.corporationName}>
                            {' '}
                            {item.corporationName}
                          </span>
                        </>
                      ) : (
                        '*****'
                      )}
                    </CompanyNameContent>
                  </StyledTableCell>
                )}
                {showNoteColumnTable && (
                  <StyledTableCell style={{ width: '150px' }}>
                    <Tooltip
                      title={
                        item.nota <= 0
                          ? 'Nota não aplicável a esse ativo ou não encontrada'
                          : `${item.nota.toFixed(0)}%`
                      }
                    >
                      {item.nota <= 0 ? (
                        <>N/A</>
                      ) : (
                        <GraphicNote
                          noteValue={item.nota}
                          color={
                            item.nota <= 50
                              ? '#ff0000'
                              : item.nota <= 60
                              ? '#ffff00'
                              : '#0000ff'
                          }
                        />
                      )}
                    </Tooltip>
                  </StyledTableCell>
                )}

                {showAmountColumnTable && (
                  <StyledTableCell>
                    {showActive || showAmountActives
                      ? item.equitiesQuantity
                      : '****'}
                  </StyledTableCell>
                )}
                {showPatrimonyColumnTable && (
                  <StyledTableCell
                    className="reduce"
                    title={
                      showActive
                        ? transformMaskCurrency(item.updateValue)
                        : 'R$ ******'
                    }
                  >
                    {showActive
                      ? transformMaskCurrency(item.updateValue)
                      : 'R$ ******'}
                  </StyledTableCell>
                )}
                {showPriceColumnTable && (
                  <StyledTableCell>
                    {transformMaskCurrency(item.closingPrice)}
                  </StyledTableCell>
                )}
                {showGoalColumnTable && (
                  //@ts-ignore
                  <RenderConditionalObjective
                    active={item}
                    exceptions={item.Exception}
                    setRule={setRule}
                    setShowModalCreateRule={setShowModalCreateRule}
                    walletValues={walletValues}
                    action
                    forceAllType={forceAllType}
                  />
                )}
                {showCurrentColumnTable && (
                  <StyledTableCell
                    style={{
                      color: defineDisplayColor({
                        item,
                        exceptions: item.Exception,
                        type: 'action',
                        //@ts-ignore
                        wallet: walletValues,
                        filterBrokerage: undefined,
                      }),
                    }}
                  >
                    {item.percent.toFixed(2)}%
                  </StyledTableCell>
                )}
                {showGapColumnTable && (
                  <StyledTableCell>{item?.gap?.toFixed(2)}%</StyledTableCell>
                )}
                {showAveragePriceColumnTable && (
                  <StyledTableCell
                    className="reduce"
                    title={
                      showActive
                        ? transformMaskCurrency(item.preco_medio_sem_proventos)
                        : 'R$ ******'
                    }
                  >
                    {showActive
                      ? transformMaskCurrency(item.preco_medio_sem_proventos)
                      : 'R$ ******'}
                  </StyledTableCell>
                )}
                {showAveragePriceWithProfitsColumnTable && (
                  <StyledTableCell
                    className="reduce"
                    title={
                      showActive
                        ? transformMaskCurrency(item.preco_medio_com_proventos)
                        : 'R$ ******'
                    }
                  >
                    {showActive
                      ? transformMaskCurrency(item.preco_medio_com_proventos)
                      : 'R$ ******'}
                  </StyledTableCell>
                )}
                {showTotalProfits && (
                  <StyledTableCell
                    className="reduce"
                    title={
                      showActive
                        ? transformMaskCurrency(item.total_proventos)
                        : 'R$ ******'
                    }
                  >
                    {showActive
                      ? transformMaskCurrency(item.total_proventos)
                      : 'R$ ******'}
                  </StyledTableCell>
                )}
                {/* {showProfitabilityColumnTable && (
                                 <StyledTableCell
                                   style={{
                                     color:
                                       item.porcentagem_rentabilidade_sem_proventos < 0
                                         ? 'red'
                                         : '#44bd32',
                                   }}
                                 >
                                   {item?.porcentagem_rentabilidade_sem_proventos
                                     ? `${item?.porcentagem_rentabilidade_sem_proventos}%`
                                     : '-'}
                                 </StyledTableCell>
                               )} */}
                {showTotalApliedColumnTable && (
                  <StyledTableCell
                    className="reduce"
                    title={
                      showActive
                        ? transformMaskCurrency(
                            item.equitiesQuantity *
                              item.preco_medio_sem_proventos
                          )
                        : 'R$ ******'
                    }
                  >
                    {showActive
                      ? transformMaskCurrency(
                          item.equitiesQuantity * item.preco_medio_sem_proventos
                        )
                      : 'R$ ******'}
                  </StyledTableCell>
                )}

                {showRealProfitabilityColumnTable && (
                  <StyledTableCell
                    style={{
                      color:
                        item.porcentagem_rentabilidade_com_proventos < 0
                          ? 'red'
                          : '#44bd32',
                    }}
                  >
                    {item?.porcentagem_rentabilidade_com_proventos
                      ? `${item?.porcentagem_rentabilidade_com_proventos}%`
                      : '-'}
                  </StyledTableCell>
                )}
                {showProfitColumnTable && (
                  <StyledTableCell
                    className="reduce"
                    title={
                      showActive
                        ? transformMaskCurrency(
                            item.valor_rentabilidade_com_proventos
                          )
                        : 'R$ ******'
                    }
                    style={{
                      color:
                        item.valor_rentabilidade_com_proventos < 0
                          ? 'red'
                          : '#44bd32',
                    }}
                  >
                    {showActive
                      ? transformMaskCurrency(
                          item.valor_rentabilidade_com_proventos
                        )
                      : 'R$ ******'}
                  </StyledTableCell>
                )}
              </StyledTableRow>
            ))
          ) : (
            <h1 style={{ fontSize: '1rem', margin: '1rem 0.5rem' }}>
              Ainda não há dados
            </h1>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
